import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../lib/utils";

export type ButtonVariant = "default" | "primary" | "danger" | "quiet" | "warning";
export type ButtonSize = "small" | "default" | "large";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
  busyLabel?: string;
  children: ReactNode;
};

/** Button presentation for elements that must stay links (router links, external anchors). */
export function buttonStyle({ variant = "default", size = "default", className }: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} = {}) {
  return { className: cn("sqk-button", className), "data-variant": variant, "data-size": size } as const;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = "default",
  size = "default",
  busy = false,
  busyLabel = "Loading",
  disabled,
  className,
  type,
  children,
  ...props
}, ref) => (
  <button
    aria-busy={busy || undefined}
    className={cn("sqk-button", className)}
    data-size={size}
    data-variant={variant}
    disabled={disabled || busy}
    ref={ref}
    type={type ?? "button"}
    {...props}
  >
    {busy ? (
      <span aria-label={busyLabel} className="sqk-button__loader" role="status">
        {Array.from({ length: 5 }, (_, index) => <i aria-hidden="true" key={index} />)}
      </span>
    ) : children}
  </button>
));
Button.displayName = "Button";
